import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Download, FileText, Package, Gift, UserRound, LogOut, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API, img, money, fmtDate, fmtSize, errMsg } from "../lib/api";
import { useAuth } from "../lib/store";
import { Empty } from "../components/Bits";
import { useSeo } from "../components/Seo";

const STATUS = { pending: ["Čeká na platbu", "bg-[var(--sun-soft)]"], paid: ["Zaplaceno", "bg-[var(--teal-soft)]"], cancelled: ["Zrušeno", "bg-gray-100"] };

function Downloads() {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/my/downloads").then((r) => setList(r.data));
  }, []);
  if (!list) return null;
  if (!list.length) return <Empty>Zatím tu nic není. <Link to="/eshop" className="underline">Vyberte si první materiál</Link></Empty>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {list.map((p) => (
        <div key={p.id} className="rounded-[24px] border border-[#f6e3ec] p-4 flex gap-4" data-testid={`download-product-${p.slug}`}>
          <img src={img(p.image)} alt="" className="w-20 h-20 rounded-2xl object-cover" />
          <div className="flex-1 min-w-0">
            <Link to={`/produkt/${p.slug}`} className="font-display text-lg">{p.name}</Link>
            {p.files.length === 0 && <p className="text-sm text-[var(--muted)]">Soubor se připravuje.</p>}
            {p.files.map((f) => (
              <a key={f.id} href={`${API}/my/download/${p.id}/${f.id}`} data-testid={`download-file-${f.id}`} className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-[var(--teal-soft)] px-3 py-2 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                <span className="flex items-center gap-2 truncate"><FileText className="w-4 h-4 shrink-0" />{f.name}</span><span className="flex items-center gap-1 shrink-0">{fmtSize(f.size)}<Download className="w-4 h-4" /></span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Orders() {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/orders/my").then((r) => setList(r.data));
  }, []);
  if (!list) return null;
  if (!list.length) return <Empty>Zatím žádné objednávky.</Empty>;
  return (
    <div className="space-y-3">
      {list.map((o) => (
        <div key={o.id} className="rounded-[24px] border border-[#f6e3ec] p-5 flex flex-wrap items-center gap-4" data-testid={`account-order-${o.number}`}>
          <div className="flex-1 min-w-[160px]"><p className="font-bold">č. {o.number}</p><p className="text-sm text-[var(--muted)]">{fmtDate(o.created_at)} · {o.items.length} položek</p></div>
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS[o.status][1]}`}>{STATUS[o.status][0]}</span>
          <span className="font-extrabold">{money(o.total)}</span>
          <Link to={`/objednavka/${o.id}`} className="btn-ghost !py-2 !px-4">Detail</Link>
          {o.invoice_number && <a href={`${API}/orders/${o.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-4"><FileDown className="w-4 h-4" /> Faktura</a>}
        </div>
      ))}
    </div>
  );
}

function MyVouchers() {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/my/vouchers").then((r) => setList(r.data));
  }, []);
  if (!list) return null;
  if (!list.length) return <Empty>Žádné dárkové poukazy. <Link to="/darkove-poukazy" className="underline">Darovat radost</Link></Empty>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {list.map((v) => (
        <div key={v.id} className="rounded-[24px] border-2 border-dashed border-[var(--pink)] p-5 bg-[var(--blush)]" data-testid={`account-voucher-${v.code}`}>
          <p className="text-3xl font-display text-[var(--pink)]">{money(v.value)}</p>
          <p className="font-bold tracking-widest mt-1">{v.code}</p>
          <p className="text-sm text-[var(--muted)]">Zůstatek {money(v.balance)} · platí do {fmtDate(v.valid_until)}</p>
          <a href={`${API}/my/vouchers/${v.code}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost !py-2 !px-4 mt-3"><FileDown className="w-4 h-4" /> PDF poukaz</a>
        </div>
      ))}
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name || "", ...(user.billing || {}) });
  const save = async () => {
    try {
      const { name, ...billing } = f;
      const { data } = await api.put("/auth/profile", { name, billing });
      setUser(data);
      toast.success("Uloženo");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const F = [["name", "Jméno"], ["company", "Firma / školka"], ["street", "Ulice"], ["city", "Město"], ["zip", "PSČ"], ["ico", "IČO"], ["dic", "DIČ"], ["phone", "Telefon"]];
  return (
    <div className="max-w-2xl">
      <p className="text-sm text-[var(--muted)] mb-4">E-mail: <b>{user.email}</b></p>
      <div className="grid sm:grid-cols-2 gap-3">{F.map(([k, l]) => <input key={k} placeholder={l} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field" data-testid={`profile-${k}-input`} />)}</div>
      <button onClick={save} className="btn-pink mt-4" data-testid="profile-save-btn">Uložit údaje</button>
    </div>
  );
}

const TABS = [["materialy", "Moje materiály", Package, Downloads], ["objednavky", "Objednávky", FileText, Orders], ["poukazy", "Poukazy", Gift, MyVouchers], ["profil", "Údaje", UserRound, Profile]];

export default function Account() {
  useSeo({ title: "Můj účet", noindex: true });
  const { user, logout } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "materialy";
  if (user === null) return <div className="h-[60vh]" />;
  if (!user) return <Navigate to="/prihlaseni?next=/ucet" />;
  const Comp = TABS.find((t) => t[0] === tab)?.[3] || Downloads;
  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><p className="eyebrow mb-1">Můj účet</p><h1 className="text-4xl sm:text-5xl font-semibold">Ahoj, {user.name || user.email.split("@")[0]}!</h1></div>
        <button onClick={logout} className="btn-ghost" data-testid="account-logout-btn"><LogOut className="w-4 h-4" /> Odhlásit</button>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setSp({ tab: k })} data-testid={`account-tab-${k}`} className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-colors ${tab === k ? "bg-[var(--pink)] text-white" : "bg-[var(--blush)] hover:bg-[var(--pink-soft)]"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>
      <Comp />
    </div>
  );
}
